import {gql} from '@apollo/client';
import {createStyles} from '@mantine/core';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import useFuzzyDate from 'ui-library/hooks/useFuzzyDate';

import {getChatNameRenderInfo} from '../ChatNameUtil';
import {
  RedesignHeaderChatTitleQuery,
  RedesignHeaderChatTitleQueryVariables,
} from './__generated/RedesignHeaderChatTitleQuery';

export const REDESIGN_HEADER_CHAT_TITLE_QUERY = gql`
  query RedesignHeaderChatTitleQuery($id: StrictString!) @engComponent(name: LLM) @skipAudit {
    chatSession(id: $id) {
      ... on ChatSessionNode {
        id
        name
        modifiedAt
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const useStyles = createStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 0,
  },
  name: {
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    lineHeight: '20px',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  subtitle: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.gray[6],
    lineHeight: '16px',
  },
}));

/**
 * The active chat's name (and last-modified time) shown centered in the redesigned sidepanel header.
 * Reads the chat session cache-first, so it resolves immediately once the chat has been opened.
 */
export default function RedesignHeaderTitle({chatId}: {chatId: string}): JSX.Element | null {
  const {classes} = useStyles();
  const result = useBenchlingQuery<RedesignHeaderChatTitleQuery, RedesignHeaderChatTitleQueryVariables>(
    REDESIGN_HEADER_CHAT_TITLE_QUERY,
    {variables: {id: chatId}, fetchPolicy: 'cache-first'}
  );
  const node =
    result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.chatSession)
      ? result.data.chatSession
      : null;
  // The fallback only satisfies the unconditional hook call while node is still null; once node resolves,
  // modifiedAt is always present, so the fuzzy date below is never derived from it.
  const fuzzyDate = useFuzzyDate(node?.modifiedAt ?? new Date().toISOString());

  if (node == null) {
    return null;
  }

  const nameInfo = getChatNameRenderInfo({id: node.id, name: node.name});
  // Own the heading role here (rather than on the wrapping header slot) so it exists only once the
  // title has resolved — while this returns null the header has no empty heading for screen readers.
  return (
    <div role="heading" aria-level={1} className={classes.root}>
      <span className={classes.name}>{nameInfo.node}</span>
      <span className={classes.subtitle}>Last modified {fuzzyDate}</span>
    </div>
  );
}
